import { useMemo } from 'react'
import {
  ViewBoxDimensions,
  ViewBoxConfig,
  ZoomConfig,
  TouchConfig,
} from '../types'
import { DEFAULT_TOUCH_CONFIG, DEFAULT_VIEWBOX_CONFIG, DEFAULT_ZOOM_CONFIG } from '../consts'

interface MobileDetection {
  isMobile: boolean
  isMobileLayout: boolean
  mobilePortait: boolean
}

interface ViewboxState {
  x: number
  y: number
  zoom: number
}

interface ViewboxConfigOptions {
  mobileDetection: MobileDetection
  viewboxState: ViewboxState
  viewboxConfig?: ViewBoxConfig
  zoomConfig?: ZoomConfig
  touchConfig?: TouchConfig
}

interface ViewboxConfigReturn {
  defaultViewBox: ViewBoxDimensions
  defaultZoom: number
  touchConfig: TouchConfig
  svgViewBoxConfig: string
  mobileOrientationZoomFactor: number
}

const clamp = (min: number, num: number, max: number) => Math.max(min, Math.min(num, max))

export const useViewboxConfig = ({
  mobileDetection,
  viewboxState,
  viewboxConfig = DEFAULT_VIEWBOX_CONFIG,
  zoomConfig = DEFAULT_ZOOM_CONFIG,
  touchConfig = DEFAULT_TOUCH_CONFIG
}: ViewboxConfigOptions): ViewboxConfigReturn => {
  const { isMobileLayout, mobilePortait } = mobileDetection
  const { x: viewboxX, y: viewboxY, zoom } = viewboxState

  const defaultViewBox = useMemo(() => {
    if (!isMobileLayout) {
      return viewboxConfig.desktop
    }

    return mobilePortait ? viewboxConfig.mobilePortrait : viewboxConfig.mobileLandscape
  }, [isMobileLayout, mobilePortait, viewboxConfig])

  const defaultZoom = useMemo(() =>
     isMobileLayout ? zoomConfig.mobile : zoomConfig.desktop,
  [isMobileLayout, zoomConfig])

  const mobileOrientationZoomFactor = useMemo(() =>  
    mobilePortait ? 7 : 4,
  [mobilePortait])

  const svgViewBoxConfig = useMemo(() => {
    if (isMobileLayout) {
      return `${viewboxX} ${viewboxY} ${clamp(
        100,
        defaultViewBox.width - zoom / 2,
        10000
      )} ${clamp(1500, zoom * mobileOrientationZoomFactor, 10000)}`
    }
    
    return `${viewboxX} ${viewboxY} ${defaultViewBox.width + zoom * 2} ${
      defaultViewBox.height + zoom * 2
    }`
  }, [isMobileLayout, viewboxX, viewboxY, defaultViewBox, zoom, mobileOrientationZoomFactor])

  return {
    defaultViewBox,
    defaultZoom,
    touchConfig,
    svgViewBoxConfig,
    mobileOrientationZoomFactor
  }
}